import { doctorBillingAgreementState } from "./doctorBillingAgreementState.mjs";
import { commissionCopy } from "../../supabase/functions/_shared/clinicianSettlementPolicy.mjs";
import supabase from "@/services/supabaseClient";
import { uploadObject } from "@/lib/storage.js";

export const DOCTOR_BILLING_LOGO_BUCKET = "doctor-profile-images";
export const DOCTOR_SIGNED_AGREEMENT_BUCKET = "signed-agreements";
export const DOCTOR_SIGNATURE_BUCKET = "doctor-assets";
export const DOCTOR_TERMS_BUCKET = "doctor-terms";
export const AFRIMED_BILLING_AGREEMENT_VERSION = "2026-09-21";
export const AFRIMED_BILLING_AGREEMENT_LOGO_PATH = "/assets/images/afrimed_logo.svg";
export const AFRIMED_FOUNDER_SIGNATURE_PATH = "/assets/images/NN-signature-AfriMed-mark.png";

const BRANDING_SELECT = [
  "signature_path",
  "practice_logo_path",
  "billing_agreement_path",
  "billing_agreement_signed_at",
  "billing_agreement_version",
  "doctor_terms_path",
  "doctor_terms_mime",
  "doctor_terms_updated_at",
].join(",");

function isMissingColumnError(error) {
  const message = String(error?.message || "").toLowerCase();
  return message.includes("column") && message.includes("doctor_profiles");
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    String(value || "").trim(),
  );
}

function getFileExtension(filename = "", fallback = "png") {
  const cleanName = String(filename || "").trim();
  const ext = cleanName.split(".").pop();
  return String(ext || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "") || fallback;
}

function slugify(value, fallback = "doctor") {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || fallback;
}

function triggerDownload(filename, payload, type) {
  const blob = payload instanceof Blob ? payload : new Blob([payload], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

async function rasterizeImageToPngDataUrl(sourceUrl = "") {
  const cleanUrl = String(sourceUrl || "").trim();
  if (!cleanUrl) return "";

  const response = await fetch(cleanUrl);
  if (!response.ok) {
    throw new Error(`Could not load image asset: ${cleanUrl}`);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);

  try {
    const image = await new Promise((resolve, reject) => {
      const nextImage = new window.Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () => reject(new Error(`Could not decode image asset: ${cleanUrl}`));
      nextImage.src = objectUrl;
    });

    const width = Math.max(1, Number(image.naturalWidth || image.width || 1));
    const height = Math.max(1, Number(image.naturalHeight || image.height || 1));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Could not create image canvas.");
    context.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL("image/png");
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function loadDoctorBrandingRow(userId) {
  const result = await supabase
    .from("doctor_profiles")
    .select(BRANDING_SELECT)
    .eq("user_id", userId)
    .maybeSingle();

  if (!result.error) return result.data || {};
  if (!isMissingColumnError(result.error)) throw result.error;
  return {};
}

async function resolvePracticeLogoUrl(path) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return "";
  const { data } = supabase.storage.from(DOCTOR_BILLING_LOGO_BUCKET).getPublicUrl(cleanPath);
  return String(data?.publicUrl || "").trim();
}

async function resolveDoctorSignatureUrl(path) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return "";

  const { data, error } = await supabase.storage
    .from(DOCTOR_SIGNATURE_BUCKET)
    .createSignedUrl(cleanPath, 60 * 10);

  if (error) return "";
  return String(data?.signedUrl || "").trim();
}

async function resolveAgreementUrl(path) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return "";

  const { data, error } = await supabase.storage
    .from(DOCTOR_SIGNED_AGREEMENT_BUCKET)
    .createSignedUrl(cleanPath, 60 * 10);

  if (error) return "";
  return String(data?.signedUrl || "").trim();
}

async function resolveDoctorTermsUrl(path) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return "";
  const { data } = supabase.storage.from(DOCTOR_TERMS_BUCKET).getPublicUrl(cleanPath);
  return String(data?.publicUrl || "").trim();
}

async function removeStorageObject(bucket, path) {
  const cleanPath = String(path || "").trim();
  if (!cleanPath) return;
  try {
    await supabase.storage.from(bucket).remove([cleanPath]);
  } catch {
    // Best-effort cleanup only.
  }
}

export async function loadDoctorBillingBranding(userId) {
  if (!String(userId || "").trim() || !isUuidLike(userId)) {
    return {
      doctorSignaturePath: "",
      doctorSignatureUrl: "",
      practiceLogoPath: "",
      practiceLogoUrl: "",
      agreementPath: "",
      agreementUrl: "",
      agreementSignedAt: "",
      agreementVersion: "",
      doctorTermsPath: "",
      doctorTermsUrl: "",
      doctorTermsMime: "",
      doctorTermsUpdatedAt: "",
    };
  }

  const row = await loadDoctorBrandingRow(userId);
  const doctorSignaturePath = String(row?.signature_path || "").trim();
  const practiceLogoPath = String(row?.practice_logo_path || "").trim();
  const agreementPath = String(row?.billing_agreement_path || "").trim();
  const doctorTermsPath = String(row?.doctor_terms_path || "").trim();
  const [doctorSignatureUrl, practiceLogoUrl, agreementUrl, doctorTermsUrl] = await Promise.all([
    resolveDoctorSignatureUrl(doctorSignaturePath),
    resolvePracticeLogoUrl(practiceLogoPath),
    resolveAgreementUrl(agreementPath),
    resolveDoctorTermsUrl(doctorTermsPath),
  ]);

  return {
    doctorSignaturePath,
    doctorSignatureUrl,
    practiceLogoPath,
    practiceLogoUrl,
    agreementPath,
    agreementUrl,
    ...doctorBillingAgreementState(row, AFRIMED_BILLING_AGREEMENT_VERSION),
    doctorTermsPath,
    doctorTermsUrl,
    doctorTermsMime: String(row?.doctor_terms_mime || "").trim(),
    doctorTermsUpdatedAt: String(row?.doctor_terms_updated_at || "").trim(),
  };
}

export async function saveDoctorPracticeLogo({
  userId,
  file,
  previousPath = "",
}) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) throw new Error("Please sign in to save your practice logo.");

  const ext = getFileExtension(file?.name, "png");
  const path = `doctors/${cleanUserId}/practice-logo-${Date.now()}.${ext}`;

  await uploadObject({
    bucket: DOCTOR_BILLING_LOGO_BUCKET,
    key: path,
    file,
    userId: cleanUserId,
    allowedMimes: [
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
    ],
    maxBytes: 5 * 1024 * 1024,
  });

  const { error } = await supabase
    .from("doctor_profiles")
    .update({ practice_logo_path: path })
    .eq("user_id", cleanUserId);

  if (error) {
    await removeStorageObject(DOCTOR_BILLING_LOGO_BUCKET, path);
    throw error;
  }

  if (previousPath && previousPath !== path) {
    await removeStorageObject(DOCTOR_BILLING_LOGO_BUCKET, previousPath);
  }

  return loadDoctorBillingBranding(cleanUserId);
}

export async function saveDoctorTermsDocument({
  userId,
  file,
  previousPath = "",
}) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) throw new Error("Please sign in to upload doctor terms.");

  const ext = getFileExtension(file?.name, "pdf");
  const path = `doctors/${cleanUserId}/doctor-terms-${Date.now()}.${ext}`;
  const uploadedAt = new Date().toISOString();
  const mime = String(file?.type || "").trim() || "application/pdf";

  await uploadObject({
    bucket: DOCTOR_TERMS_BUCKET,
    key: path,
    file,
    userId: cleanUserId,
    allowedMimes: [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "image/webp",
    ],
    maxBytes: 10 * 1024 * 1024,
  });

  const { error } = await supabase
    .from("doctor_profiles")
    .update({
      doctor_terms_path: path,
      doctor_terms_mime: mime,
      doctor_terms_updated_at: uploadedAt,
    })
    .eq("user_id", cleanUserId);

  if (error) {
    await removeStorageObject(DOCTOR_TERMS_BUCKET, path);
    throw error;
  }

  if (previousPath && previousPath !== path) {
    await removeStorageObject(DOCTOR_TERMS_BUCKET, previousPath);
  }

  return loadDoctorBillingBranding(cleanUserId);
}

export async function loadDoctorTermsDisclosure(doctorRef) {
  const cleanDoctorRef = String(doctorRef || "").trim();
  if (!cleanDoctorRef) {
    return {
      doctorTermsPath: "",
      doctorTermsUrl: "",
      doctorTermsMime: "",
      doctorTermsUpdatedAt: "",
    };
  }

  const result = await supabase
    .from("doctor_profiles")
    .select("doctor_terms_path,doctor_terms_mime,doctor_terms_updated_at")
    .or(`user_id.eq.${cleanDoctorRef},id.eq.${cleanDoctorRef}`)
    .maybeSingle();

  if (result.error) {
    if (isMissingColumnError(result.error)) {
      return {
        doctorTermsPath: "",
        doctorTermsUrl: "",
        doctorTermsMime: "",
        doctorTermsUpdatedAt: "",
      };
    }
    throw result.error;
  }

  const doctorTermsPath = String(result.data?.doctor_terms_path || "").trim();
  const doctorTermsUrl = await resolveDoctorTermsUrl(doctorTermsPath);

  return {
    doctorTermsPath,
    doctorTermsUrl,
    doctorTermsMime: String(result.data?.doctor_terms_mime || "").trim(),
    doctorTermsUpdatedAt: String(result.data?.doctor_terms_updated_at || "").trim(),
  };
}

export async function uploadDoctorSignedAgreement({
  userId,
  file,
  previousPath = "",
  version = AFRIMED_BILLING_AGREEMENT_VERSION,
}) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) throw new Error("Please sign in to upload the signed agreement.");

  const cleanVersion = slugify(version, "current");
  const ext = getFileExtension(file?.name, "pdf");
  const path = `doctors/${cleanUserId}/billing-agreement-${cleanVersion}-${Date.now()}.${ext}`;
  const signedAt = new Date().toISOString();

  await uploadObject({
    bucket: DOCTOR_SIGNED_AGREEMENT_BUCKET,
    key: path,
    file,
    userId: cleanUserId,
    allowedMimes: [
      "application/pdf",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "text/html",
    ],
    maxBytes: 10 * 1024 * 1024,
  });

  const { error } = await supabase
    .from("doctor_profiles")
    .update({
      billing_agreement_path: path,
      billing_agreement_signed_at: signedAt,
      billing_agreement_version: version,
    })
    .eq("user_id", cleanUserId);

  if (error) {
    await removeStorageObject(DOCTOR_SIGNED_AGREEMENT_BUCKET, path);
    throw error;
  }

  if (previousPath && previousPath !== path) {
    await removeStorageObject(DOCTOR_SIGNED_AGREEMENT_BUCKET, previousPath);
  }

  return loadDoctorBillingBranding(cleanUserId);
}

async function resolveAgreementLogoMarkup(logoUrlOverride = "") {
  const overrideUrl = String(logoUrlOverride || "").trim();
  if (overrideUrl) {
    return `<img src="${overrideUrl}" alt="AfriMed logo" style="height:44px;width:auto;" />`;
  }

  const logoUrl = `${window.location.origin}${AFRIMED_BILLING_AGREEMENT_LOGO_PATH}`;

  try {
    const response = await fetch(AFRIMED_BILLING_AGREEMENT_LOGO_PATH);
    if (!response.ok) throw new Error("Logo fetch failed.");
    const svg = await response.text();
    return `<div style="width: 164px; height: 44px;">${svg}</div>`;
  } catch {
    return `<img src="${logoUrl}" alt="AfriMed logo" style="height:44px;width:auto;" />`;
  }
}

function resolveFounderSignatureMarkup(founderSignatureUrlOverride = "") {
  const founderSignatureUrl =
    String(founderSignatureUrlOverride || "").trim() ||
    `${window.location.origin}${AFRIMED_FOUNDER_SIGNATURE_PATH}`;
  return `<img src="${founderSignatureUrl}" alt="Founder signature" crossorigin="anonymous" style="height:64px;width:auto;object-fit:contain;" />`;
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

async function buildDoctorBillingAgreementHtml({
  doctorName = "",
  practiceName = "",
  marketLabel = "",
  version = AFRIMED_BILLING_AGREEMENT_VERSION,
  doctorSignatureUrl = "",
  agreementSignedAt = "",
  agreementLogoUrl = "",
  founderSignatureUrl = "",
  renderMode = "preview",
}) {
  const isPdfRender = renderMode === "pdf";
  const logoMarkup = await resolveAgreementLogoMarkup(agreementLogoUrl);
  const founderSignatureMarkup = resolveFounderSignatureMarkup(founderSignatureUrl);
  const issuedOn = new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());
  const doctorSignatureMarkup = String(doctorSignatureUrl || "").trim()
    ? `<img src="${doctorSignatureUrl}" alt="Doctor signature" crossorigin="anonymous" style="height:64px;width:auto;object-fit:contain;" />`
    : `<div style="height:64px;display:flex;align-items:flex-end;color:#5b6b86;font-size:13px;">Doctor signature will appear here once saved in AfriMed™.</div>`;
  const signedOn = agreementSignedAt
    ? new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }).format(new Date(agreementSignedAt))
    : "Pending doctor sign-off";

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>AfriMed Doctor Billing Agreement</title>
    <style>
      body {
        margin: 0;
        background: #ffffff;
        color: #040812;
        font-family: Inter, "Segoe UI", sans-serif;
      }
      .page {
        max-width: ${isPdfRender ? "880px" : "820px"};
        margin: 0 auto;
        padding: ${isPdfRender ? "0" : "32px 20px 48px"};
        background: #ffffff;
      }
      .sheet {
        background: #ffffff;
        border-radius: ${isPdfRender ? "0" : "18px"};
        padding: ${isPdfRender ? "16px 16px 20px" : "32px"};
        box-shadow: ${isPdfRender ? "none" : "0 10px 30px rgba(4, 8, 18, 0.08)"};
      }
      .top-logo {
        display: flex;
        justify-content: flex-end;
        margin-bottom: ${isPdfRender ? "8px" : "12px"};
      }
      .top-logo > * {
        display: block;
      }
      .eyebrow {
        color: #5b6b86;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.02em;
      }
      h1 {
        margin: ${isPdfRender ? "8px 0 10px" : "10px 0 12px"};
        font-size: ${isPdfRender ? "22px" : "24px"};
        line-height: ${isPdfRender ? "1.1" : "1.15"};
        color: #040812;
      }
      h2 {
        margin: ${isPdfRender ? "18px 0 8px" : "28px 0 10px"};
        font-size: ${isPdfRender ? "16px" : "18px"};
        line-height: 1.25;
        color: #040812;
      }
      p, li {
        color: #233146;
        font-size: ${isPdfRender ? "14px" : "15px"};
        line-height: ${isPdfRender ? "1.45" : "1.6"};
      }
      .meta {
        margin-top: ${isPdfRender ? "14px" : "18px"};
        display: grid;
        gap: ${isPdfRender ? "8px" : "10px"};
        grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
      }
      .meta-card, .sign-card {
        border: 1px solid rgba(91, 107, 134, 0.14);
        border-radius: 12px;
        padding: ${isPdfRender ? "12px 14px" : "14px 16px"};
        background: #fbfcfe;
      }
      .label {
        color: #5b6b86;
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .label-brand-mixed {
        text-transform: none;
      }
      .value {
        margin-top: 6px;
        font-size: ${isPdfRender ? "14px" : "15px"};
        color: #040812;
      }
      ul {
        margin: ${isPdfRender ? "8px 0 0" : "12px 0 0"};
        padding-left: 20px;
      }
      .sign-grid {
        display: grid;
        gap: ${isPdfRender ? "10px" : "16px"};
        margin-top: ${isPdfRender ? "18px" : "24px"};
        grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
      }
      .line {
        margin-top: ${isPdfRender ? "24px" : "40px"};
        border-top: 1px solid rgba(4, 8, 18, 0.28);
        padding-top: ${isPdfRender ? "8px" : "10px"};
        color: #5b6b86;
        font-size: 13px;
      }
      @media (max-width: 520px) {
        .top-logo {
          justify-content: flex-start;
        }
        h1 {
          font-size: 22px;
          line-height: 1.2;
        }
      }
      @media print {
        body {
          background: #ffffff;
        }
        .page {
          padding: 0;
        }
        .sheet {
          border-radius: 0;
          box-shadow: none;
          padding: 24px;
        }
      }
    </style>
  </head>
  <body>
    <main class="page">
      <section class="sheet">
        <div class="top-logo">${logoMarkup}</div>
        <div class="eyebrow">AfriMed™ direct-care billing lane</div>
        <h1>Doctor Billing &amp; Commission Agreement</h1>
        <p>
          This agreement pack is branded with the AfriMed™ documents identity on purpose. Practice logos
          belong on doctor-issued documents such as invoices, receipts, and prescriptions, while the platform
          agreement itself remains under AfriMed™ branding.
        </p>

        <div class="meta">
          <div class="meta-card">
            <div class="label">Agreement version</div>
            <div class="value">${escapeHtml(version)}</div>
          </div>
          <div class="meta-card">
            <div class="label">Issued on</div>
            <div class="value">${escapeHtml(issuedOn)}</div>
          </div>
          <div class="meta-card">
            <div class="label">Doctor</div>
            <div class="value">${escapeHtml(doctorName || "Doctor to complete")}</div>
          </div>
          <div class="meta-card">
            <div class="label">Practice</div>
            <div class="value">${escapeHtml(practiceName || "Practice to complete")}</div>
          </div>
          <div class="meta-card">
            <div class="label">Primary market</div>
            <div class="value">${escapeHtml(marketLabel || "SADC / diaspora lane")}</div>
          </div>
        </div>

        <h2>Commercial model</h2>
        <ul>
          <li>AfriMed™ does not require an upfront doctor subscription to start taking direct-care bookings.</li>
          <li>${commissionCopy.en}</li>
          <li>${commissionCopy.fr}</li>
        </ul>

        <h2>Service, availability, and payment alignment</h2>
        <ul>
          <li>Availability controls when patients can book.</li>
          <li>Services & pricing controls what can be booked, for how long, and at what price.</li>
          <li>Consultancy controls where in-person bookings happen.</li>
          <li>Virtual consultation remains a consult mode that consumes availability instead of replacing it.</li>
          <li>AfriMed™ may support sponsor and diaspora payment links so family-funded healthcare remains frictionless and trustworthy.</li>
        </ul>

        <h2>Doctor assets and branding</h2>
        <ul>
          <li>The doctor’s practice logo is stored separately for future use on invoices, receipts, and prescriptions.</li>
          <li>The doctor’s saved prescription signature remains reusable for prescriber workflows and can be referenced alongside this agreement state.</li>
          <li>AfriMed™ founder sign-off is represented below with the platform signature mark so the agreement preview reflects the official platform document branding.</li>
        </ul>

        <div class="sign-grid">
          <div class="sign-card">
            <div class="label">Doctor signature</div>
            <div style="margin-top:16px;">${doctorSignatureMarkup}</div>
            <div class="line">${escapeHtml(doctorName || "Doctor to complete")}</div>
          </div>
          <div class="sign-card">
            <div class="label">Date signed</div>
            <div class="line">${escapeHtml(signedOn)}</div>
          </div>
          <div class="sign-card">
            <div class="label label-brand-mixed">AfriMed™ FOUNDER SIGN-OFF</div>
            <div style="margin-top:16px;">${founderSignatureMarkup}</div>
            <div class="line">Founder &amp; Innovation Lead</div>
          </div>
          <div class="sign-card">
            <div class="label">Practice name</div>
            <div class="line">${escapeHtml(practiceName || "Practice to complete")}</div>
          </div>
        </div>
      </section>
    </main>
  </body>
</html>`;
}

export async function previewDoctorBillingAgreementPack(options = {}) {
  return buildDoctorBillingAgreementHtml(options);
}

async function generateDoctorBillingAgreementPdfBlob(options = {}) {
  const [logoDataUrl, founderSignatureDataUrl, doctorSignatureDataUrl] = await Promise.all([
    rasterizeImageToPngDataUrl(
      `${window.location.origin}${AFRIMED_BILLING_AGREEMENT_LOGO_PATH}`
    ).catch(() => ""),
    rasterizeImageToPngDataUrl(
      `${window.location.origin}${AFRIMED_FOUNDER_SIGNATURE_PATH}`
    ).catch(() => ""),
    String(options?.doctorSignatureUrl || "").trim()
      ? rasterizeImageToPngDataUrl(options.doctorSignatureUrl).catch(() => "")
      : Promise.resolve(""),
  ]);

  const html = await buildDoctorBillingAgreementHtml({
    ...options,
    agreementLogoUrl: logoDataUrl,
    founderSignatureUrl: founderSignatureDataUrl,
    doctorSignatureUrl:
      doctorSignatureDataUrl || String(options?.doctorSignatureUrl || "").trim(),
    renderMode: "pdf",
  });

  const parsedDocument = new window.DOMParser().parseFromString(html, "text/html");
  const mountNode = document.createElement("div");
  mountNode.style.position = "fixed";
  mountNode.style.left = "-10000px";
  mountNode.style.top = "0";
  mountNode.style.width = "860px";
  mountNode.style.background = "#ffffff";
  mountNode.style.pointerEvents = "none";
  mountNode.style.zIndex = "-1";

  const styleNode = document.createElement("style");
  styleNode.textContent = Array.from(parsedDocument.head.querySelectorAll("style"))
    .map((node) => node.textContent || "")
    .join("\n");
  mountNode.appendChild(styleNode);

  const contentNode = document.createElement("div");
  contentNode.innerHTML = parsedDocument.body.innerHTML;
  mountNode.appendChild(contentNode);
  document.body.appendChild(mountNode);

  try {
    const pageNode = mountNode.querySelector(".page") || mountNode;
    const images = Array.from(pageNode.querySelectorAll("img"));
    await Promise.all(
      images.map(
        (image) =>
          new Promise((resolve) => {
            if (image.complete && image.naturalWidth > 0) {
              resolve();
              return;
            }
            const done = () => resolve();
            image.addEventListener("load", done, { once: true });
            image.addEventListener("error", done, { once: true });
          })
      )
    );
    try {
      await document.fonts?.ready;
    } catch {
      // Ignore font readiness failures and continue with available fonts.
    }
    await new Promise((resolve) => window.setTimeout(resolve, 80));

    const html2canvasMod = await import("html2canvas");
    const html2canvas = html2canvasMod?.default || html2canvasMod;
    const fullCanvas = await html2canvas(pageNode, {
      scale: 2,
      backgroundColor: "#ffffff",
      useCORS: false,
      logging: false,
      windowWidth: Math.ceil(pageNode.scrollWidth),
      windowHeight: Math.ceil(pageNode.scrollHeight),
    });

    const jsPdfMod = await import("jspdf");
    const JsPdf = jsPdfMod?.jsPDF || jsPdfMod?.default;
    const pdf = new JsPdf({ unit: "pt", format: "a4" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 16;
    const renderWidth = pageWidth - margin * 2;
    const renderHeight = pageHeight - margin * 2;
    const sliceHeightPx = Math.max(
      1,
      Math.floor((renderHeight * fullCanvas.width) / renderWidth)
    );

    let offsetY = 0;
    let pageIndex = 0;
    while (offsetY < fullCanvas.height) {
      const currentSliceHeight = Math.min(
        sliceHeightPx,
        fullCanvas.height - offsetY
      );
      const sliceCanvas = document.createElement("canvas");
      sliceCanvas.width = fullCanvas.width;
      sliceCanvas.height = currentSliceHeight;
      const context = sliceCanvas.getContext("2d");
      if (!context) throw new Error("Could not create PDF slice canvas.");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      context.drawImage(
        fullCanvas,
        0,
        offsetY,
        fullCanvas.width,
        currentSliceHeight,
        0,
        0,
        fullCanvas.width,
        currentSliceHeight
      );

      if (pageIndex > 0) pdf.addPage();
      const renderedSliceHeight =
        (currentSliceHeight * renderWidth) / fullCanvas.width;
      pdf.addImage(
        sliceCanvas.toDataURL("image/png"),
        "PNG",
        margin,
        margin,
        renderWidth,
        renderedSliceHeight,
        undefined,
        "FAST"
      );

      offsetY += currentSliceHeight;
      pageIndex += 1;
    }

    return pdf.output("blob");
  } finally {
    document.body.removeChild(mountNode);
  }
}

function buildAgreementPdfFilename({
  doctorName = "",
  practiceName = "",
  version = AFRIMED_BILLING_AGREEMENT_VERSION,
}) {
  return `afrimed-doctor-billing-agreement-${slugify(
    doctorName || practiceName || "doctor"
  )}-${slugify(version, "current")}.pdf`;
}

export async function signAndArchiveDoctorBillingAgreement({
  userId,
  previousPath = "",
  doctorName = "",
  practiceName = "",
  marketLabel = "",
  version = AFRIMED_BILLING_AGREEMENT_VERSION,
  doctorSignatureUrl = "",
}) {
  const cleanUserId = String(userId || "").trim();
  if (!cleanUserId) throw new Error("Please sign in to sign the agreement.");
  if (!String(doctorSignatureUrl || "").trim()) {
    throw new Error("Please save your doctor signature before signing the agreement.");
  }

  const signedAt = new Date().toISOString();
  const pdfBlob = await generateDoctorBillingAgreementPdfBlob({
    doctorName,
    practiceName,
    marketLabel,
    version,
    doctorSignatureUrl,
    agreementSignedAt: signedAt,
  });
  const cleanVersion = slugify(version, "current");
  const path = `doctors/${cleanUserId}/billing-agreement-${cleanVersion}-${Date.now()}.pdf`;

  await uploadObject({
    bucket: DOCTOR_SIGNED_AGREEMENT_BUCKET,
    key: path,
    file: pdfBlob,
    userId: cleanUserId,
    allowedMimes: ["application/pdf"],
    maxBytes: 10 * 1024 * 1024,
  });

  const { error } = await supabase
    .from("doctor_profiles")
    .update({
      billing_agreement_path: path,
      billing_agreement_signed_at: signedAt,
      billing_agreement_version: version,
    })
    .eq("user_id", cleanUserId);

  if (error) {
    await removeStorageObject(DOCTOR_SIGNED_AGREEMENT_BUCKET, path);
    throw error;
  }

  if (previousPath && previousPath !== path) {
    await removeStorageObject(DOCTOR_SIGNED_AGREEMENT_BUCKET, previousPath);
  }

  return loadDoctorBillingBranding(cleanUserId);
}

export async function downloadSignedDoctorBillingAgreementCopy({
  agreementUrl = "",
  doctorName = "",
  practiceName = "",
  version = AFRIMED_BILLING_AGREEMENT_VERSION,
}) {
  const filename = buildAgreementPdfFilename({ doctorName, practiceName, version });
  const cleanUrl = String(agreementUrl || "").trim();

  if (cleanUrl) {
    const response = await fetch(cleanUrl);
    if (!response.ok) throw new Error("Could not download the signed agreement copy.");
    const blob = await response.blob();
    triggerDownload(filename, blob, "application/pdf");
    return;
  }

  const pdfBlob = await generateDoctorBillingAgreementPdfBlob({
    doctorName,
    practiceName,
    version,
  });
  triggerDownload(filename, pdfBlob, "application/pdf");
}

export async function downloadDoctorBillingAgreementPack(options = {}) {
  const { doctorName = "", practiceName = "", version = AFRIMED_BILLING_AGREEMENT_VERSION } = options;
  const pdfBlob = await generateDoctorBillingAgreementPdfBlob(options);
  const filename = buildAgreementPdfFilename({ doctorName, practiceName, version });
  triggerDownload(filename, pdfBlob, "application/pdf");
}

export default {
  AFRIMED_BILLING_AGREEMENT_LOGO_PATH,
  AFRIMED_BILLING_AGREEMENT_VERSION,
  AFRIMED_FOUNDER_SIGNATURE_PATH,
  DOCTOR_SIGNATURE_BUCKET,
  DOCTOR_TERMS_BUCKET,
  downloadSignedDoctorBillingAgreementCopy,
  downloadDoctorBillingAgreementPack,
  loadDoctorBillingBranding,
  loadDoctorTermsDisclosure,
  previewDoctorBillingAgreementPack,
  saveDoctorPracticeLogo,
  saveDoctorTermsDocument,
  signAndArchiveDoctorBillingAgreement,
  uploadDoctorSignedAgreement,
};
